import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlogPosts } from '../lib/api';
import { useSEO } from '../lib/seo';
import './BlogPage.css';

const CATEGORIES = [
  { key: 'all',            label: 'All Posts' },
  { key: 'plant-care',     label: 'Plant Care' },
  { key: 'lifestyle',      label: 'Lifestyle' },
  { key: 'guides',         label: 'Guides' },
  { key: 'sustainability', label: 'Sustainability' },
];

const CATEGORY_LABEL = CATEGORIES.reduce((acc, c) => {
  acc[c.key] = c.label;
  return acc;
}, {});

/* ── Hero icons ── */
function LeafMarkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M4 20C4 20 3 11 9 6C15 1 21 3 21 3C21 3 22 11 16 16C10 21 4 20 4 20Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M4 20C4 20 8 15 13 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function ClipIcon() {
  return (
    <svg className="bp-hero__card-clip" width="20" height="34" viewBox="0 0 20 34" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6 8V25C6 28.3137 8.68629 31 12 31C15.3137 31 18 28.3137 18 25V6C18 3.79086 16.2091 2 14 2C11.7909 2 10 3.79086 10 6V24C10 25.1046 10.8954 26 12 26C13.1046 26 14 25.1046 14 24V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function BlogPage() {
  useSEO({
    title: 'Blog — Plant Care Tips, Guides & Lifestyle',
    description: 'Read expert plant care guides, indoor gardening tips and sustainability insights from Naturiq.',
    url: '/blog',
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    getBlogPosts()
      .then(data => { if (active) setPosts(data.posts || []); })
      .catch(() => { if (active) setError('Could not load the journal right now.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const sorted = useMemo(
    () => [...posts].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [posts]
  );

  const featured = sorted[0];

  const filtered = useMemo(() => {
    return sorted.slice(1).filter(p => {
      const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
      const matchesQuery = !query.trim() ||
        p.title.toLowerCase().includes(query.trim().toLowerCase()) ||
        p.excerpt.toLowerCase().includes(query.trim().toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [sorted, activeCategory, query]);

  return (
    <main className="bp-list">

      {/* ══════════════════════════════════════
          HERO — "The Pressed-Leaf Archive"
          A field-journal card catalog, not a
          gradient-and-glass hero.
      ══════════════════════════════════════ */}
      <section className="bp-hero">

        {/* Fine dot-grid, like graph paper under a lamp */}
        <div className="bp-hero__texture" aria-hidden />

        {/* One quiet botanical line — signature, not scenery */}
        <svg className="bp-hero__leafline" viewBox="0 0 200 420" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M96 410C96 410 18 338 13 232C8 126 66 46 66 46" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          <path d="M50 222C50 222 17 206 9 174" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          <path d="M63 158C63 158 30 145 21 115" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          <path d="M76 94C76 94 46 83 38 56" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        </svg>

        <div className="bp-hero__inner">

          {/* LEFT — masthead, title, search */}
          <div className="bp-hero__intro">
            <div className="bp-hero__mark">
              <LeafMarkIcon />
              <span>Naturiq — Garden Journal</span>
            </div>

            <h1 className="bp-hero__title">
              The Garden
              <br />
              <em>Journal</em>
            </h1>

            <p className="bp-hero__sub">
              Care notes, monsoon survival tactics, and the odd bit of
              nonsense — filed weekly by people who spend more time with
              leaves than people.
            </p>

            <div className="bp-hero__search-wrap">
              <label className="bp-hero__search" htmlFor="bp-search">
                <span className="bp-hero__search-label">Search</span>
                <input
                  id="bp-search"
                  type="text"
                  placeholder="the archive…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  aria-label="Search blog posts"
                />
                {query ? (
                  <button
                    type="button"
                    className="bp-hero__search-clear"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                ) : (
                  <svg className="bp-hero__search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                )}
              </label>
            </div>

            {!loading && posts.length > 0 && (
              <p className="bp-hero__tally">
                {posts.length} {posts.length === 1 ? 'entry' : 'entries'} on record · updated weekly
              </p>
            )}
          </div>

          {/* RIGHT — latest post, filed like a catalog card */}
          <div className="bp-hero__catalog" aria-hidden={loading || !featured}>
            {loading ? (
              <div className="bp-hero__card-stack">
                <span className="bp-hero__card-shadow bp-hero__card-shadow--a" aria-hidden />
                <span className="bp-hero__card-shadow bp-hero__card-shadow--b" aria-hidden />
                <div className="bp-hero__card-sk">
                  <div className="bp-hero__card-sk-img" />
                  <div className="bp-hero__card-sk-body">
                    <div className="bp-hero__card-sk-line bp-hero__card-sk-line--sm" />
                    <div className="bp-hero__card-sk-line bp-hero__card-sk-line--lg" />
                    <div className="bp-hero__card-sk-line bp-hero__card-sk-line--md" />
                  </div>
                </div>
              </div>
            ) : featured ? (
              <div className="bp-hero__card-stack">
                <span className="bp-hero__card-shadow bp-hero__card-shadow--a" aria-hidden />
                <span className="bp-hero__card-shadow bp-hero__card-shadow--b" aria-hidden />
                <Link to={`/blog/${featured.slug}`} className="bp-hero__card" tabIndex={0}>
                  <ClipIcon />
                  <div className="bp-hero__card-img-wrap">
                    <img src={featured.cover} alt={featured.title} className="bp-hero__card-img" loading="eager" />
                    <span className="bp-hero__card-tape bp-hero__card-tape--l" aria-hidden />
                    <span className="bp-hero__card-tape bp-hero__card-tape--r" aria-hidden />
                  </div>
                  <div className="bp-hero__card-body">
                    <div className="bp-hero__card-topline">
                      <span className="bp-hero__card-num">No. {String(posts.length).padStart(3, '0')}</span>
                      <span className="bp-hero__card-cat">{CATEGORY_LABEL[featured.category] || featured.category}</span>
                    </div>
                    <h2 className="bp-hero__card-title">{featured.title}</h2>
                    <p className="bp-hero__card-excerpt">{featured.excerpt}</p>
                    <div className="bp-hero__card-footer">
                      <div className="bp-hero__card-meta">
                        {featured.author?.avatar && (
                          <img src={featured.author.avatar} alt={featured.author.name} className="bp-hero__card-avatar" />
                        )}
                        <span>{featured.author?.name}</span>
                      </div>
                      <span className="bp-hero__card-read">{featured.readTime} min read</span>
                    </div>
                  </div>
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </section>
      {/* ══ end hero ══ */}

      {loading ? (
        <div className="bp-skel-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bp-skel-card">
              <div className="bp-skel-card__img skel" />
              <div className="bp-skel-card__body">
                <div className="skel" style={{ width: '30%', height: 11, borderRadius: 4 }} />
                <div className="skel" style={{ width: '90%', height: 18, borderRadius: 6, marginTop: 8 }} />
                <div className="skel" style={{ width: '70%', height: 18, borderRadius: 6, marginTop: 4 }} />
                <div className="skel" style={{ width: '80%', height: 12, borderRadius: 4, marginTop: 10 }} />
                <div className="skel" style={{ width: '55%', height: 12, borderRadius: 4, marginTop: 4 }} />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bp-empty"><p>{error}</p></div>
      ) : posts.length === 0 ? (
        <div className="bp-empty"><p>No posts published yet — check back soon.</p></div>
      ) : (
        <>
          {/* ── Featured post (full card) ── */}
          {!query && featured && (
            <section className="bp-featured">
              <Link to={`/blog/${featured.slug}`} className="bp-featured__card">
                <div className="bp-featured__img-wrap">
                  <img src={featured.cover} alt={featured.title} className="bp-featured__img" loading="eager" />
                </div>
                <div className="bp-featured__body">
                  <span className="bp-tag">{CATEGORY_LABEL[featured.category] || featured.category}</span>
                  <h2 className="bp-featured__title">{featured.title}</h2>
                  <p className="bp-featured__excerpt">{featured.excerpt}</p>
                  <div className="bp-featured__meta">
                    <img src={featured.author?.avatar} alt={featured.author?.name} className="bp-featured__avatar" />
                    <span>{featured.author?.name}</span>
                    <span className="bp-dot">·</span>
                    <span>{formatDate(featured.date)}</span>
                    <span className="bp-dot">·</span>
                    <span>{featured.readTime} min read</span>
                  </div>
                </div>
              </Link>
            </section>
          )}

          {/* ── Filters ── */}
          <section className="bp-filters">
            <div className="bp-filters__row">
              {CATEGORIES.map(c => (
                <button
                  key={c.key}
                  className={`bp-chip${activeCategory === c.key ? ' bp-chip--active' : ''}`}
                  onClick={() => setActiveCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {/* ── Grid ── */}
          <section className="bp-grid-section">
            {filtered.length === 0 ? (
              <div className="bp-empty">
                <p>Nothing here yet — try a different category or search term.</p>
              </div>
            ) : (
              <div className="bp-grid">
                {filtered.map(post => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="bp-card">
                    <div className="bp-card__img-wrap">
                      <img src={post.cover} alt={post.title} className="bp-card__img" loading="lazy" />
                      <span className="bp-card__tag">{CATEGORY_LABEL[post.category] || post.category}</span>
                    </div>
                    <div className="bp-card__body">
                      <h3 className="bp-card__title">{post.title}</h3>
                      <p className="bp-card__excerpt">{post.excerpt}</p>
                      <div className="bp-card__meta">
                        <span>{formatDate(post.date)}</span>
                        <span className="bp-dot">·</span>
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* ── Newsletter ── */}
      <section className="bp-news">
        <div className="bp-news__inner">
          <h2 className="bp-news__title">Get a little greener every week</h2>
          <p className="bp-news__sub">One email a week — a care tip, a reader question, and whatever's blooming at the farm.</p>
          <form className="bp-news__form" onSubmit={e => e.preventDefault()}>
            <input type="email" placeholder="you@example.com" required aria-label="Email address" />
            <button type="submit">Subscribe</button>
          </form>
        </div>
      </section>

    </main>
  );
}